import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { TbRefresh } from "react-icons/tb";
import { PageHeaderWithBack } from "../../../../Components/Molecules";
import {
  GRID_HISTORY_COLS_6,
  GRID_HISTORY_HEADER_VALUE_CLASS,
  GridHistoryCell,
  GridHistoryContainer,
  GridHistoryHeader,
  GridHistoryRow,
  GridPagination,
} from "../../../../Components/Molecules/GridHistory";
import { getStoredSelectedAccountId, IB_ACTIVE_ACCOUNT_CHANGED_EVENT } from "../../../../Constants/accountTypes";
import useActiveIbAccount from "../../../../Hooks/useActiveIbAccount";
import { useToast } from "../../../../Hooks/toast";
import { listPixBatchesForAccounts } from "../../../../Services/pixBatchesApi";
import { fetchOperatingAccounts } from "../../../../Services/operatingAccountsApi";
import { isIbHomeSelectableAccount } from "../../../../Utils/accountPixEligibility";
import { formatCurrency } from "../../../../Utils/formatters";
import { paginateList } from "../../Extract/extractFilters";

const STATUS_TABS = [
  { id: "all", label: "Todos" },
  { id: "processing", label: "Processando" },
  { id: "completed", label: "Concluídos" },
  { id: "failed", label: "Falhas" },
];

const LIST_POLL_MS = 5000;

const formatDate = (value) => {
  if (!value) return "—";
  return new Intl.DateTimeFormat("pt-BR", { dateStyle: "short", timeStyle: "short" }).format(
    new Date(value),
  );
};

const normalizeStatus = (status) => String(status || "").trim().toLowerCase();

const BatchStatusBadge = ({ status }) => {
  const key = normalizeStatus(status);
  const styles = {
    created: "bg-gray-100 text-gray-600 dark:bg-brand-dark-elevated dark:text-gray-300",
    processing: "bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400",
    completed: "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400",
    failed: "bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-400",
    partial: "bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400",
  };
  const labels = {
    created: "Criado",
    processing: "Processando",
    completed: "Concluído",
    failed: "Falhou",
    partial: "Parcial",
  };
  return (
    <span
      className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${styles[key] ?? styles.processing}`}
    >
      {labels[key] ?? (status || "—")}
    </span>
  );
};

const countLegProgress = (batch) => {
  const legs = Array.isArray(batch?.legs) ? batch.legs : [];
  const total = Number(batch?.leg_count) || legs.length || 0;
  if (!total) return { done: 0, total: 0, pct: 0 };
  const done = legs.filter((leg) => {
    const s = normalizeStatus(leg?.status);
    return (
      s === "completed"
      || s === "success"
      || s === "confirmed"
      || s === "accepted"
      || s === "failed"
      || s === "rejected"
    );
  }).length;
  return { done, total, pct: Math.min(100, Math.round((done / total) * 100)) };
};

const resolveListAccountIds = async (activeAccountId) => {
  const ids = [];
  const push = (value) => {
    const id = String(value || "").trim();
    if (id && !ids.includes(id)) ids.push(id);
  };

  push(activeAccountId);
  push(getStoredSelectedAccountId());

  try {
    const accounts = await fetchOperatingAccounts();
    (Array.isArray(accounts) ? accounts : [])
      .filter((row) => isIbHomeSelectableAccount(row))
      .forEach((row) => push(row?.id || row?.account_id));
  } catch {
    // Active / stored id alone is enough to attempt the list.
  }

  return ids;
};

export const PixLotesPage = () => {
  const { addToast } = useToast();
  const { account: activeIbAccount, loading: accountLoading } = useActiveIbAccount();
  const activeAccountId = activeIbAccount?.id || getStoredSelectedAccountId();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [statusTab, setStatusTab] = useState("all");
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const refresh = useCallback(async ({ silent = false } = {}) => {
    if (accountLoading) {
      return;
    }

    if (!silent) setLoading(true);
    setLoadError(null);
    try {
      const accountIds = await resolveListAccountIds(activeAccountId);
      if (accountIds.length === 0) {
        setItems([]);
        setLoadError(null);
        return;
      }
      const data = await listPixBatchesForAccounts(accountIds);
      setItems(data.items);
    } catch (error) {
      setItems([]);
      const message = error?.message || "Não foi possível carregar os lotes Pix.";
      setLoadError(message);
      if (!silent) {
        addToast({
          type: "error",
          title: message,
        });
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [accountLoading, activeAccountId, addToast]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    const hasActive = items.some((item) => {
      const status = normalizeStatus(item?.status);
      return status === "processing" || status === "created" || status === "partial";
    });
    if (accountLoading || !hasActive) return undefined;
    const timer = setInterval(() => {
      refresh({ silent: true });
    }, LIST_POLL_MS);
    return () => clearInterval(timer);
  }, [accountLoading, items, refresh]);

  useEffect(() => {
    const onAccountChanged = () => {
      refresh();
    };
    window.addEventListener(IB_ACTIVE_ACCOUNT_CHANGED_EVENT, onAccountChanged);
    return () => window.removeEventListener(IB_ACTIVE_ACCOUNT_CHANGED_EVENT, onAccountChanged);
  }, [refresh]);

  useEffect(() => {
    setPage(1);
  }, [statusTab, activeAccountId]);

  const filteredItems = useMemo(() => {
    if (statusTab === "all") return items;
    return items.filter((item) => {
      const status = normalizeStatus(item?.status);
      if (statusTab === "processing") {
        return status === "processing" || status === "created" || status === "partial";
      }
      if (statusTab === "completed") return status === "completed";
      if (statusTab === "failed") return status === "failed" || status === "partial";
      return true;
    });
  }, [items, statusTab]);

  const emptyMessage = accountLoading
    ? "Carregando contas…"
    : !activeAccountId
      ? "Selecione uma conta para ver os lotes Pix."
      : loadError
        ? loadError
        : statusTab !== "all" && items.length > 0
          ? "Nenhum lote neste filtro."
          : "Nenhum lote Pix encontrado.";
  const paginatedItems = useMemo(
    () => paginateList(filteredItems, page, rowsPerPage),
    [filteredItems, page, rowsPerPage],
  );

  const showLoading = loading || accountLoading;

  return (
    <div className="pix-lotes min-w-0 w-full space-y-5">
      <PageHeaderWithBack title="Lotes Pix" subTitle="Acompanhe envios divididos por limite transacional." />

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2" role="tablist" aria-label="Filtrar por status">
          {STATUS_TABS.map((tab) => {
            const active = statusTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={active}
                className={`rounded-lg border px-3 py-1.5 text-xs font-medium transition ${
                  active
                    ? "border-[#e7a73f] bg-[#e7a73f]/15 text-brand-dark dark:text-gray-100"
                    : "border-gray-200 text-gray-600 hover:border-gray-300 dark:border-brand-dark-border dark:text-gray-300"
                }`}
                onClick={() => setStatusTab(tab.id)}
              >
                {tab.label}
              </button>
            );
          })}
        </div>
        <button type="button" className="btn-outline w-auto shrink-0" onClick={() => refresh()} disabled={showLoading}>
          <TbRefresh className="h-4 w-4" aria-hidden />
          Atualizar
        </button>
      </div>

      <GridHistoryContainer gridCols={GRID_HISTORY_COLS_6}>
        <GridHistoryHeader colSpan={6}>
          <span>Quando</span>
          <span>Destinatário</span>
          <span>Status</span>
          <span>Progresso</span>
          <span className={GRID_HISTORY_HEADER_VALUE_CLASS}>Valor</span>
          <span className="text-right">Detalhe</span>
        </GridHistoryHeader>
        {showLoading ? (
          <p className="col-span-6 px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
            Carregando lotes…
          </p>
        ) : loadError ? (
          <p className="col-span-6 px-4 py-8 text-center text-sm text-red-600 dark:text-red-400">
            {loadError}
          </p>
        ) : paginatedItems.length === 0 ? (
          <p className="col-span-6 px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
            {emptyMessage}
          </p>
        ) : (
          paginatedItems.map((batch) => {
            const batchId = batch.batch_id ?? batch.batchId;
            const progress = countLegProgress(batch);
            return (
              <GridHistoryRow key={batchId} colSpan={6}>
                <GridHistoryCell label="Quando">
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {formatDate(batch.created_at ?? batch.createdAt)}
                  </p>
                </GridHistoryCell>
                <GridHistoryCell label="Destinatário">
                  <p className="truncate text-sm font-semibold text-brand-dark dark:text-gray-100">
                    {batch.receiver_name ?? batch.receiverName ?? batch.key ?? "—"}
                  </p>
                  {batch.key ? (
                    <p className="truncate text-xs text-gray-400">{batch.key}</p>
                  ) : null}
                </GridHistoryCell>
                <GridHistoryCell label="Status">
                  <BatchStatusBadge status={batch.status} />
                </GridHistoryCell>
                <GridHistoryCell label="Progresso">
                  <div className="min-w-[120px] space-y-1">
                    <div className="h-1.5 w-full rounded-full bg-gray-100 dark:bg-brand-dark-elevated">
                      <div
                        className="h-1.5 rounded-full bg-[#e7a73f] transition-all"
                        style={{ width: `${progress.pct}%` }}
                      />
                    </div>
                    <p className="text-[11px] text-gray-500 dark:text-gray-400">
                      {progress.done}/{progress.total || (batch.leg_count ?? batch.legCount ?? 0)} Pix
                    </p>
                  </div>
                </GridHistoryCell>
                <GridHistoryCell label="Valor" align="right">
                  <p className="text-sm font-semibold tabular-nums text-brand-dark dark:text-gray-100">
                    {formatCurrency(batch.total_amount ?? batch.totalAmount ?? 0)}
                  </p>
                  <p className="text-xs text-gray-400">
                    {batch.leg_count ?? batch.legCount ?? 0} parcelas
                  </p>
                </GridHistoryCell>
                <div className="flex items-center justify-end">
                  <Link
                    to={`/pix/lotes/${encodeURIComponent(batchId)}${
                      batch.account_id || batch.accountId
                        ? `?account_id=${encodeURIComponent(batch.account_id || batch.accountId)}`
                        : ""
                    }`}
                    className="text-xs font-semibold text-[#D3A971] no-underline hover:opacity-80"
                  >
                    Ver lote
                  </Link>
                </div>
              </GridHistoryRow>
            );
          })
        )}
      </GridHistoryContainer>

      <GridPagination
        total={filteredItems.length}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={setPage}
        onRowsPerPageChange={(next) => {
          setRowsPerPage(next);
          setPage(1);
        }}
        currentPageCount={paginatedItems.length}
        itemLabel="lotes"
      />
    </div>
  );
};
